import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { productApi } from '#services/product';
import { revertAll } from '../actions';

interface ShopListState {
  filter: string;
  filterKey: string;
  order: string;
  orderBy: string;
  pageIndex: number;
  rows: unknown[];
  rowCount: number;
  pageSize: number;
  collections: unknown[];
  tags: unknown[];
  selectedCollections: unknown[];
  selectedTags: unknown[];
  userId: string | number | null;
}

interface GetProductsFulfilledAction {
  payload: unknown;
  meta: { arg: { originalArgs: unknown } };
}

const initialState: ShopListState = {
  filter: '',
  filterKey: 'name',
  order: 'desc',
  orderBy: 'featured',
  pageIndex: 0,
  rows: [],
  rowCount: 0,
  pageSize: 20,

  collections: [],
  tags: [],
  selectedCollections: [],
  selectedTags: [],
  userId: null,
};

function toggleArray(array: unknown[], item: unknown) {
  const index = array.indexOf(item);
  if (index === -1) {
    // not exists
    array.push(item);
  } else {
    array.splice(index, 1);
  }
}

const arraysEqual = (a: unknown[] = [], b: unknown[] = []) => (
  a.length === b.length && a.every((v, i) => v === b[i])
);

const handleGetProductsFulfilled = (
  state: ShopListState,
  action: GetProductsFulfilledAction,
) => {
  const { page, rows, total } = action.payload as {
    page: number;
    rows: unknown[];
    total: number;
  };
  const args = action.meta.arg.originalArgs as {
    userId: string | number | null;
    order: string;
    orderBy: string;
    filter: string;
    filterKey: string;
    collections: unknown[];
    tags: unknown[];
    pageIndex: number;
  };

  // All non-page dimensions must match the current state before accepting
  // any response — including server-corrected (out-of-range) ones. A stale
  // corrected response for an old filter/tag/collection must not overwrite
  // the visible rows even though its page differs from what was requested.
  const dimensionsMatch = (
    args.userId === state.userId
    && args.order === state.order
    && args.orderBy === state.orderBy
    && args.filter === state.filter
    && args.filterKey === state.filterKey
    && arraysEqual(args.collections, state.selectedCollections)
    && arraysEqual(args.tags, state.selectedTags)
  );

  if (!dimensionsMatch) {
    return;
  }

  const serverCorrected = page !== args.pageIndex;

  if (serverCorrected) {
    // Dimensions match and server redirected to page 0 (out-of-range).
    state.pageIndex = page;
    state.rows = rows;
    state.rowCount = total;
  } else if (args.pageIndex === state.pageIndex) {
    // Dimensions and page both match — safe to update.
    state.rows = rows;
    state.rowCount = total;
  }
  // Otherwise: stale page response — discard.
};

export const shopListSlice = createSlice({
  name: 'shopList',
  initialState,
  reducers: {
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
    },
    setFilterKey(state, action: PayloadAction<string>) {
      state.filterKey = action.payload;
    },
    setOrder(state, action: PayloadAction<string>) {
      state.order = action.payload;
    },
    setOrderBy(state, action: PayloadAction<string>) {
      state.orderBy = action.payload;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageIndex = 0;
    },
    setRows(state, action: PayloadAction<unknown[]>) {
      state.rows = action.payload;
    },
    setRowCount(state, action: PayloadAction<number>) {
      state.rowCount = action.payload;
    },

    setSelectedCollections(state, action: PayloadAction<unknown[]>) {
      state.selectedCollections = action.payload;
    },
    setSelectedTags(state, action: PayloadAction<unknown[]>) {
      state.selectedTags = action.payload;
    },
    toggleSelectedCollections(state, action: PayloadAction<unknown>) {
      toggleArray(state.selectedCollections, action.payload);
      state.pageIndex = 0;
    },
    toggleSelectedTags(state, action: PayloadAction<unknown>) {
      toggleArray(state.selectedTags, action.payload);
      state.pageIndex = 0;
    },
    setUserId(state, action: PayloadAction<string | number | null>) {
      state.userId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertAll, () => initialState)
      .addMatcher(productApi.endpoints.getProducts.matchFulfilled, handleGetProductsFulfilled)
      .addMatcher(productApi.endpoints.deleteProducts.matchFulfilled, handleGetProductsFulfilled)
      .addMatcher(productApi.endpoints.uploadProducts.matchFulfilled, handleGetProductsFulfilled);
  },
});

export const {
  setFilter,
  setFilterKey,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setRows,
  setRowCount,

  setSelectedCollections,
  setSelectedTags,
  toggleSelectedCollections,
  toggleSelectedTags,
  setUserId,
} = shopListSlice.actions;

export default shopListSlice.reducer;
